/**
 * One member of this clinic: who they are, and what they can reach.
 *
 * Two sections because they are two different things, changed for two different
 * reasons. The profile is the person - their name, and their address while it is
 * still ours to correct. The grants are their access.
 *
 * A grant can be suspended, and a store user can be moved to another store. The
 * *role* cannot be changed here: clinic admin and store user live in different
 * tables, so switching between them is a removal and a new grant, and hiding a
 * privilege change inside an edit is how someone ends up with access nobody
 * remembers granting.
 */
import { ROLE_LABEL } from '../roles.ts';
import { useState } from 'react';
import { Link, useLoaderData, useOutletContext } from 'react-router';
import type { MemberDetail, Session } from '../api.ts';
import { patch } from '../api.ts';
import {
  Card,
  Detail,
  Details,
  Empty,
  Pill,
  StatusPill,
  Table,
  jaDate,
} from '../components/ui.tsx';
import { EditPanel, Field } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';


export default function MemberDetailPage() {
  const member = useLoaderData<MemberDetail>();
  const session = useOutletContext<Session>();
  const clinic = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  const stores = clinic?.stores ?? [];
  const isSelf = member.userId === session.user.id;

  const [editingProfile, setEditingProfile] = useState(false);
  const [profile, setProfile] = useState({
    displayName: member.displayName,
    email: member.email ?? '',
  });

  /** Which grant's panel is open, by grant id. Only ever one at a time. */
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState({ status: 'ACTIVE', storeId: '' });
  const grant = member.grants.find((g) => g.id === open);

  const saveProfile = () =>
    patch(`/members/${member.userId}/profile`, {
      displayName: profile.displayName,
      // Sending an unchanged address on a claimed account would be refused, and
      // refusing an edit nobody made is not helpful.
      ...(member.claimed ? {} : { email: profile.email }),
    });

  function edit(id: string) {
    const target = member.grants.find((g) => g.id === id);
    if (!target) return;
    setForm({ status: target.status, storeId: target.store_id ?? '' });
    setOpen(id);
  }

  const saveGrant = () =>
    patch(`/members/${member.userId}`, {
      grantId: open,
      status: form.status,
      // Only a store grant can move; a clinic-wide one has no store to move to.
      ...(grant?.store_id ? { storeId: form.storeId } : {}),
    });

  return (
    <>
      <Card
        title={member.displayName || (member.email ?? 'ユーザー')}
        caption="このクリニックのユーザーです。"
        actions={
          <>
            <Link className="btn btn--sm btn--ghost" to="/members">
              一覧に戻る
            </Link>
            {!editingProfile && (
              <button className="btn btn--sm" type="button" onClick={() => setEditingProfile(true)}>
                編集
              </button>
            )}
          </>
        }
      >
        <Details>
          <Detail label="表示名">{member.displayName || '—'}</Detail>
          <Detail
            label="メールアドレス"
            hint={
              member.claimed
                ? 'このユーザーはすでにログイン済みです。メールアドレスはご本人のGoogleアカウントのものなので、変更できません。'
                : 'まだ一度もログインしていません。このアドレスでログインした人がこのアカウントを引き継ぎます。今のうちなら修正できます。'
            }
          >
            {member.email ?? '—'}
          </Detail>
          <Detail label="ログイン状況">
            {member.claimed ? (
              <>
                <Pill tone="ok">ログイン済み</Pill>{' '}
                <span className="muted">最終ログイン {jaDate(member.lastLoginAt)}</span>
              </>
            ) : (
              <Pill tone="warn">未ログイン</Pill>
            )}
          </Detail>
          <Detail label="アカウント状態">
            <StatusPill status={member.accountStatus} />
          </Detail>
        </Details>

        <EditPanel
          open={editingProfile}
          onClose={() => setEditingProfile(false)}
          onSubmit={saveProfile}
        >
          <Field label="表示名" htmlFor="m-name" required>
            <input
              id="m-name"
              value={profile.displayName}
              maxLength={200}
              required
              onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
            />
          </Field>
          {member.claimed ? (
            <Field
              label="メールアドレス"
              htmlFor="m-email"
              hint="ログイン済みのため変更できません。"
            >
              <input id="m-email" value={member.email ?? ''} disabled readOnly />
            </Field>
          ) : (
            <Field
              label="メールアドレス"
              htmlFor="m-email"
              hint="このアドレスでGoogleログインした人が、このアカウントと権限を引き継ぎます。"
            >
              <input
                id="m-email"
                type="email"
                value={profile.email}
                maxLength={320}
                required
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              />
            </Field>
          )}
        </EditPanel>
      </Card>

      <Card
        title="このクリニックでの権限"
        caption="行の「変更」で、権限の停止や担当店舗の変更ができます。権限の種類そのものを変えるときは、一度解除してから追加してください。"
      >
        {member.grants.length === 0 ? (
          <Empty>権限がありません。</Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">権限</th>
                <th scope="col">対象</th>
                <th scope="col">状態</th>
                <th scope="col">付与日</th>
                <th scope="col">操作</th>
              </>
            }
          >
            {member.grants.map((g) => (
              <tr key={g.id}>
                <td>{ROLE_LABEL[g.role] ?? g.role}</td>
                <td className="muted">
                  {g.store_name ?? <Pill tone="muted">クリニック全体</Pill>}
                </td>
                <td>
                  <StatusPill status={g.status} />
                </td>
                <td>{jaDate(g.created_at)}</td>
                <td>
                  <button
                    className="btn btn--sm btn--ghost"
                    type="button"
                    onClick={() => edit(g.id)}
                  >
                    変更
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}

        <EditPanel
          open={open !== null}
          onClose={() => setOpen(null)}
          onSubmit={saveGrant}
          submitLabel="この権限を更新する"
        >
          <Field
            label="状態"
            htmlFor="g-status"
            hint={
              isSelf
                ? '自分自身の権限は停止できません。'
                : '停止すると、このユーザーのログイン中のセッションはすべて即時に無効化されます。'
            }
          >
            <Select
              id="g-status"
              value={form.status}
              onChange={(status) => setForm({ ...form, status })}
              options={[
                { value: 'ACTIVE', label: '有効' },
                { value: 'DISABLED', label: '停止' },
              ]}
            />
          </Field>

          {grant?.store_id && (
            <Field label="担当店舗" htmlFor="g-store" hint="別の店舗に付け替えます。">
              <Select
                id="g-store"
                value={form.storeId}
                onChange={(storeId) => setForm({ ...form, storeId })}
                options={stores.map((store) => ({ value: store.id, label: store.name }))}
              />
            </Field>
          )}
        </EditPanel>
      </Card>
    </>
  );
}
